// Vuelta de Google, Apple o Facebook (documento 20, decisiones D48 y D51): la API deja en
// `/entrar/<proveedor>#c=<código de un uso>` (o `#error=…`) y acá se canjea por la sesión. La primera vez lleva a
// "Completá tus datos"; si no, a donde iba. Si volvía de vincular la cuenta desde "Tu cuenta" (`t=vincular`), termina el
// vínculo con la sesión que ya hay y vuelve a Perfil.
import { useEffect } from 'react';
import { useNavigate } from 'react-router';
import { rutaVolverTienda } from '../compartido';
import { Cargando } from '../componentes/base';
import { mensajeDeError } from '../datos/api';
import { entrarConProveedor, terminarVinculo } from '../datos/cuenta';
import {
  mensajeVueltaGoogle,
  NOMBRE_PROVEEDOR,
  type Proveedor,
  proveedorDeRuta,
  tomarVueltaGoogle,
  type VueltaGoogle as Vuelta,
} from '../datos/google';
import { cargarMarca } from '../datos/marca';
import { tomarVolver } from '../datos/volver';

interface Destino {
  ir: string;
  estado?: Record<string, unknown>;
}

/** Aviso que Perfil muestra al volver de vincular. */
export interface AvisoVinculo {
  vinculo: { ok: boolean; texto: string };
}

async function procesar(p: Proveedor, v: Vuelta): Promise<Destino> {
  if (v.vincular) {
    const aviso = (ok: boolean, texto: string): Destino => ({
      ir: '/perfil',
      estado: { vinculo: { ok, texto } } satisfies AvisoVinculo,
    });
    if (v.error || !v.codigo) return aviso(false, mensajeVueltaGoogle(v.error ?? 'google', p));
    try {
      const r = await terminarVinculo(p, v.codigo);
      return aviso(
        true,
        `Listo: la próxima vez también entrás con ${NOMBRE_PROVEEDOR[p]}${r.email ? ` (${r.email})` : ''}.`,
      );
    } catch (e) {
      return aviso(false, mensajeDeError(e));
    }
  }
  const fallo = (texto: string): Destino => ({ ir: '/entrar', estado: { error: texto, origen: 'google' } });
  if (v.error) {
    if (v.error === 'tienda_cerrada') void cargarMarca();
    return fallo(mensajeVueltaGoogle(v.error, p));
  }
  if (!v.codigo) return fallo(mensajeVueltaGoogle('google', p));
  try {
    const r = await entrarConProveedor(p, v.codigo);
    if (r.estado === 'falta_completar') return { ir: '/registro' };
    // Venía del carrito a confirmar (decisión D49): vuelve ahí y se confirma si nada cambió.
    const destino = tomarVolver();
    return destino
      ? { ir: destino.ruta, estado: { confirmar: destino.confirmar } }
      : { ir: rutaVolverTienda(r.volver) };
  } catch (e) {
    return fallo(mensajeDeError(e));
  }
}

// La vuelta llega siempre como una carga completa de la página: se lee apenas abre y se canjea una sola vez, aunque la
// pantalla se monte dos veces (modo estricto de React).
const proveedorInicial = typeof location !== 'undefined' ? proveedorDeRuta(location.pathname) : null;
let pendiente: Vuelta | null = proveedorInicial ? tomarVueltaGoogle() : null;
let enCurso: Promise<Destino> | null = null;

function resolver(): Promise<Destino> {
  if (!enCurso) {
    const p = proveedorDeRuta(location.pathname) ?? proveedorInicial ?? 'google';
    const v = pendiente ?? tomarVueltaGoogle();
    pendiente = null;
    enCurso = v ? procesar(p, v) : Promise.resolve({ ir: '/entrar' });
  }
  return enCurso;
}

export function VueltaGoogle() {
  const navegar = useNavigate();
  const p = proveedorDeRuta(location.pathname) ?? 'google';
  useEffect(() => {
    let vivo = true;
    void resolver().then((d) => {
      if (!vivo) return;
      enCurso = null;
      navegar(d.ir, { replace: true, state: d.estado });
    });
    return () => {
      vivo = false;
    };
  }, [navegar]);
  return <Cargando texto={`Entrando con ${NOMBRE_PROVEEDOR[p]}…`} />;
}
